/**
 * Shared "reveal choreography" primitive (async-matching UX, issue #22, W3).
 *
 * The boot -> wow -> settled load-moment state machine + its session-seen memory,
 * and the incremental-arrival id-diff, that BOTH reveal surfaces drive their motion
 * with:
 *   - the event-details "For you" section (`src/components/live/ForYouSection.tsx`)
 *   - the async-matching match arrival (`src/components/matching/MatchReveal.tsx`)
 *
 * Extracted verbatim from ForYouSection's `useLoadMomentPhase` / `useNewMatchArrivals`
 * so the two surfaces share ONE tuned machine rather than a drift-prone
 * re-implementation. The founder's ruling is that the For-you reveal is the product's
 * motion standard; sharing the machine is how the match arrival inherits it without a
 * silent fork of the subtle rules (consume-on-wow, session-seen, arrival-vs-backfill).
 *
 * SCOPE: this module owns only the framework logic. The CSS utilities and the tuned
 * DURATION LEVERS stay per-surface: ForYouSection keeps its `for-you-*` Tailwind
 * utilities + `LIVE_EVENTS_CONFIG` levers (its motion suite pins those class/var
 * names), and MatchReveal carries the same tuned values under match-specific names.
 */
import { useEffect, useRef, useState } from 'react';
import { usePrefersReducedMotion } from '@hooks/usePrefersReducedMotion';
import { defineStoredValue, PRESENCE } from '@utils/storedValue';

/**
 * `boot` = the PRE-PAINT pose (surface holds its closed/hidden state); `wow` =
 * released, every transition of the load sequence in flight; `settled` = landed,
 * accents gone, cascade delays dropped, forever. Mounts that must not replay the
 * moment (already seen this session, or reduced motion) START at `settled`.
 */
export type RevealPhase = 'boot' | 'wow' | 'settled';

/**
 * The session-seen memory behind every load-moment / enter-animation gate (the reveal
 * surfaces here and the live pill). Keyed by the FULL storage key - callers own their
 * namespace (`for-you-entered:`, `match-revealed:`, `live-pill-entered:`) and pass
 * `prefix + id`. Storage unavailable: appearances can't be remembered, so it fails toward
 * the calm default (reads `true` = render settled) instead of animating on every view.
 */
export const enteredThisSession = defineStoredValue({
  storage: 'session',
  key: (fullKey: string) => fullKey,
  ...PRESENCE,
  unreadable: true,
});

export interface RevealPhaseOptions {
  /**
   * sessionStorage namespace for the load-moment gate, keyed per surface. The stored
   * key is `keyPrefix + sessionKey` (e.g. `for-you-entered:<eventId>`,
   * `match-revealed:<questionId>`). One prefix per surface; the gate is evaluated per
   * mount, mirroring the live pill's enter rule.
   */
  keyPrefix: string;
  /**
   * When the load moment has fully landed (its last-finishing track): the wow accents
   * and cascade delays drop here. Derived by the caller from its own duration levers so
   * retuning them keeps the settle honest.
   */
  settleAfterMs: number;
}

/**
 * The per-mount reveal state machine. `sessionKey` identifies the thing being revealed
 * (an event, a question); `resolved` flips true once the surface's data is ready to show.
 *
 * Recorded once per session per `keyPrefix + sessionKey`, when the moment is actually
 * CONSUMED (not merely when data resolves):
 *   - boot mounts consume it when the wow RELEASES (`phase === 'wow'`), so a mount that
 *     resolves then unmounts before the moment paints (a StrictMode throwaway with cached
 *     data, a fast tab switch) never burns the key - the next real mount still plays.
 *   - settled-start mounts (session-seen or reduced motion) have no wow to wait for, so
 *     they consume it on resolve (session-seen idempotently re-writes the key it already
 *     read; a reduced-motion view still counts as this session's moment).
 * An unmount WHILE LOADING (never resolved, never released) leaves it untouched.
 */
export function useRevealPhase(
  sessionKey: string,
  resolved: boolean,
  options: RevealPhaseOptions,
): RevealPhase {
  const { keyPrefix, settleAfterMs } = options;
  const storageKey = keyPrefix + sessionKey;
  const reducedMotion = usePrefersReducedMotion();
  // Lazy init, evaluated once per mount (remount with a React `key` if the identity
  // changes on a mounted surface). The reduced-motion read is already correct on the
  // first render because `useMediaQuery` seeds its state synchronously from `matchMedia`.
  const [phase, setPhase] = useState<RevealPhase>(() =>
    reducedMotion || enteredThisSession.read(storageKey) ? 'settled' : 'boot',
  );
  // Did THIS mount START settled (session-seen or reduced motion)? Captured once at first
  // render - it decides WHEN the session moment is recorded (below).
  const startedSettledRef = useRef(phase === 'settled');

  useEffect(() => {
    if (!resolved || phase !== 'boot') {
      return undefined;
    }
    // THE BOOT-RACE RULE (spec 3.1): the body must COMMIT AND PAINT in its boot pose
    // before the release flips the classes, or the transitions race to nothing. Double
    // rAF guarantees one full frame in the boot pose before `wow`.
    let second = 0;
    const first = requestAnimationFrame(() => {
      second = requestAnimationFrame(() => {
        setPhase('wow');
      });
    });
    return () => {
      cancelAnimationFrame(first);
      cancelAnimationFrame(second);
    };
  }, [resolved, phase]);

  useEffect(() => {
    const consumed = startedSettledRef.current ? resolved : phase === 'wow';
    if (!consumed) {
      return;
    }
    enteredThisSession.write(true, storageKey);
  }, [resolved, phase, storageKey]);

  useEffect(() => {
    if (phase !== 'wow') {
      return undefined;
    }
    const id = window.setTimeout(() => {
      setPhase('settled');
    }, settleAfterMs);
    return () => {
      window.clearTimeout(id);
    };
  }, [phase, settleAfterMs]);

  return phase;
}

const EMPTY_ID_SET: ReadonlySet<string> = new Set();

interface ArrivalTracker {
  /** Join of the current ids - the change detector. */
  key: string;
  /** Ids first seen on THIS data change - they mount with the insert spring. */
  freshIds: ReadonlySet<string>;
  /** Every id seen since mount, visible or capped, so a backfill (a known item sliding
   * into the visible slice) never reads as an arrival. */
  knownIds: ReadonlySet<string>;
  /** Bumps once per wave of fresh ids - keys the one-shot perimeter ripple. */
  wave: number;
}

/**
 * Detects genuinely NEW ids arriving on a refetch (the mock's "New match arrives") so
 * their rows can MOUNT in the preinsert pose - this must be a render-phase derivation,
 * because a pose applied post-mount is the boot race all over again. Guarded
 * render-phase setState per the React docs' previous-render pattern (house rule): the
 * stored key converges on the current one, so the setter self-disables.
 *
 * Callers pass the ordered id list of the currently-rendered rows (For you: the
 * question ids; match arrival: the helper user ids).
 */
export function useIncrementalArrivals(ids: readonly string[]): {
  freshIds: ReadonlySet<string>;
  wave: number;
} {
  const key = ids.join('\n');
  const [tracker, setTracker] = useState<ArrivalTracker>(() => ({
    key,
    freshIds: EMPTY_ID_SET,
    knownIds: new Set(ids),
    wave: 0,
  }));

  if (tracker.key !== key) {
    const fresh = ids.filter(id => !tracker.knownIds.has(id));
    setTracker({
      key,
      freshIds: fresh.length > 0 ? new Set(fresh) : EMPTY_ID_SET,
      knownIds: new Set([...tracker.knownIds, ...ids]),
      wave: fresh.length > 0 ? tracker.wave + 1 : tracker.wave,
    });
  }

  return { freshIds: tracker.freshIds, wave: tracker.wave };
}
